import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  RESOURCES_LINK_PLACEHOLDER,
  RESOURCES_MENU,
  isResourcesLeaf,
} from "../data/resourcesMenu";
import "./ResourcesMenuDialog.css";

function collectBranchIds(nodes, acc = new Set()) {
  for (const node of nodes || []) {
    if (!isResourcesLeaf(node)) {
      acc.add(node.id);
      collectBranchIds(node.children, acc);
    }
  }
  return acc;
}

const ALL_SUBJECTS_TAB_ID = "__all__";

/** Unique subject labels across O Level + A Level (stable menu order). */
function collectSubjectTabs(menu) {
  const seen = new Set();
  const tabs = [{ id: ALL_SUBJECTS_TAB_ID, label: "All subjects" }];
  for (const level of menu || []) {
    for (const subject of level.children || []) {
      if (seen.has(subject.label)) continue;
      seen.add(subject.label);
      tabs.push({ id: subject.label, label: subject.label });
    }
  }
  return tabs;
}

function subjectDomId(levelId, subjectLabel) {
  return `resources-subject-${levelId}-${subjectLabel
    .toLowerCase()
    .replace(/\s+/g, "-")}`;
}

function openIdsForAllSubjects() {
  const ids = new Set();
  for (const level of RESOURCES_MENU) {
    collectBranchIds(level.children || [], ids);
  }
  return ids;
}

/** Open only the chosen subject (and its nested branches) on both levels. */
function openIdsForSubject(subjectLabel) {
  const ids = new Set();
  for (const level of RESOURCES_MENU) {
    const subject = (level.children || []).find(
      (node) => node.label === subjectLabel,
    );
    if (!subject || isResourcesLeaf(subject)) continue;
    ids.add(subject.id);
    collectBranchIds(subject.children || [], ids);
  }
  return ids;
}

function LeafLink({ node, prominent = false }) {
  const href = node.href || RESOURCES_LINK_PLACEHOLDER;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`resources-menu__link${
        prominent ? " resources-menu__link--prominent" : ""
      }`}
    >
      <span className="resources-menu__link-text">{node.label}</span>
      <span className="resources-menu__link-meta" aria-hidden="true">
        <i className="fa-solid fa-arrow-up-right-from-square" />
      </span>
    </a>
  );
}

function ResourcesMenuTree({
  nodes,
  levelId,
  depth = 0,
  openIds,
  onToggle,
  flashSubjectLabel,
}) {
  const renderList = (list, listDepth) => (
    <ul
      className={`resources-menu__list resources-menu__list--depth-${listDepth}`}
      role={listDepth === 0 ? "list" : "group"}
    >
      {list.map((node) => {
        if (isResourcesLeaf(node)) {
          /* Top-level leaf under a level (e.g. A Level Accounting) */
          if (listDepth === 0) {
            return (
              <li
                key={node.id}
                id={subjectDomId(levelId, node.label)}
                className={`resources-menu__item resources-menu__item--leaf resources-menu__item--subject${
                  flashSubjectLabel === node.label ? " is-flash" : ""
                }`}
              >
                <LeafLink node={node} prominent />
              </li>
            );
          }

          return (
            <li
              key={node.id}
              className="resources-menu__item resources-menu__item--leaf"
            >
              <LeafLink node={node} />
            </li>
          );
        }

        const expanded = openIds.has(node.id);
        const isSubjectRoot = listDepth === 0;

        return (
          <li
            key={node.id}
            id={isSubjectRoot ? subjectDomId(levelId, node.label) : undefined}
            className={`resources-menu__item resources-menu__item--branch${
              isSubjectRoot ? " resources-menu__item--subject" : ""
            }${expanded ? " is-open" : ""}${
              isSubjectRoot && flashSubjectLabel === node.label
                ? " is-flash"
                : ""
            }`}
          >
            <button
              type="button"
              className="resources-menu__branch"
              aria-expanded={expanded}
              onClick={() => onToggle(node.id)}
            >
              <span className="resources-menu__chevron" aria-hidden="true" />
              <span className="resources-menu__branch-label">{node.label}</span>
            </button>
            {expanded && renderList(node.children, listDepth + 1)}
          </li>
        );
      })}
    </ul>
  );

  if (!nodes?.length) {
    return (
      <p className="resources-menu-dialog__empty">No resources in this section.</p>
    );
  }

  return renderList(nodes, depth);
}

export default function ResourcesMenuDialog({ open, onClose }) {
  const titleId = useId();
  const dialogRef = useRef(null);
  const bodyRef = useRef(null);
  const closeBtnRef = useRef(null);

  const subjectTabs = useMemo(
    () => collectSubjectTabs(RESOURCES_MENU),
    [],
  );

  const [openIds, setOpenIds] = useState(() => openIdsForAllSubjects());
  const [activeSubject, setActiveSubject] = useState(ALL_SUBJECTS_TAB_ID);
  const [flashSubjectLabel, setFlashSubjectLabel] = useState("");

  const toggle = useCallback((id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const navigateToSubject = useCallback((tabId) => {
    setActiveSubject(tabId);
    if (tabId === ALL_SUBJECTS_TAB_ID) {
      setOpenIds(openIdsForAllSubjects());
      setFlashSubjectLabel("");
    } else {
      setOpenIds(openIdsForSubject(tabId));
      setFlashSubjectLabel(tabId);
    }
    bodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!flashSubjectLabel) return undefined;
    const timer = window.setTimeout(() => setFlashSubjectLabel(""), 1200);
    return () => window.clearTimeout(timer);
  }, [flashSubjectLabel]);

  useEffect(() => {
    if (!open) return undefined;

    setActiveSubject(ALL_SUBJECTS_TAB_ID);
    setOpenIds(openIdsForAllSubjects());
    setFlashSubjectLabel("");
    bodyRef.current?.scrollTo({ top: 0 });

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="resources-menu-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="resources-menu-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="resources-menu-dialog__header">
          <div>
            <p className="resources-menu-dialog__eyebrow">Browse</p>
            <h2 id={titleId} className="resources-menu-dialog__title">
              Resources
            </h2>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            className="resources-menu-dialog__close"
            onClick={onClose}
            aria-label="Close resources menu"
          >
            ×
          </button>
        </header>

        <div
          className="resources-menu-dialog__tabs"
          role="navigation"
          aria-label="Jump to subject"
        >
          {subjectTabs.map((tab) => {
            const selected = tab.id === activeSubject;
            return (
              <button
                key={tab.id}
                type="button"
                className={`resources-menu-dialog__tab${
                  selected ? " is-active" : ""
                }`}
                aria-current={selected ? "true" : undefined}
                onClick={() => navigateToSubject(tab.id)}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div ref={bodyRef} className="resources-menu-dialog__body">
          <div className="resources-menu-dialog__columns">
            {RESOURCES_MENU.map((level) => (
              <section
                key={level.id}
                className="resources-menu-dialog__column"
                aria-label={level.label}
              >
                <h3 className="resources-menu-dialog__level">{level.label}</h3>
                <ResourcesMenuTree
                  nodes={level.children || []}
                  levelId={level.id}
                  openIds={openIds}
                  onToggle={toggle}
                  flashSubjectLabel={flashSubjectLabel}
                />
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
